"use memo"

import { useQueryClient } from '@tanstack/react-query'
import {
  AutopilotTaskDto,
  TaskCategoryTaskType,
} from '@urbanx/agx.activities.client'
import {
  CampaignDetailModel,
  CampaignDetailModelState,
  CommissionDetails,
  ListingAgreementDetails,
  StaffUpdateAgreementDetailsForCampaignRequest,
  StaffUpdateCommissionInfoForCampaignRequest,
  StaffUpdateCommissionInfoForCampaignRequestChangeType,
  useInvalidateUseStaffGetAgreementDetailsForCampaign,
  useInvalidateUseStaffGetCommissionInfoForCampaign,
  useStaffGetAgreementDetailsForCampaign,
  useStaffGetCommissionInfoForCampaign,
  useStaffGetDocumentsForCampaign,
  useStaffUpdateAgreementDetailsForCampaign,
  useStaffUpdateCommissionInfoForCampaign,
} from '@urbanx/agx.campaigns.client'
import { AgxLoadingState } from '@urbanx/agx.ui-components2'
import dayjs from 'dayjs'
import { createCampaignDetailsQueryKey } from 'hooks/useSelectedCampaign'
import { filterDocumentGroups } from 'pages/campaigns/components/campaignTasks/AgreementCompliance/AgreementComplianceHelper'
import { useEffect, useState } from 'react'
import CompliancePages from './CompliancePages'
import { TaskModalPageProps } from './TaskModalPageProps'

interface UseAgreementComplianceModalProps {
  campaignId: string
  campaign: CampaignDetailModel
  isOpen: boolean
  tasks: AutopilotTaskDto[]
}

export const useAgreementComplianceModal = ({
  campaignId,
  campaign,
  isOpen,
  tasks,
}: UseAgreementComplianceModalProps) => {
  const queryClient = useQueryClient()
  const documentsEnabled = Boolean(campaign?.campaignId) && isOpen
  const { data: documentGroups, isPending: documentsPending } =
    useStaffGetDocumentsForCampaign(
      campaign?.campaignId || '',
      documentsEnabled
    )

  const filteredDocuments =
    documentGroups && Array.isArray(documentGroups)
      ? filterDocumentGroups(documentGroups)
      : []

  const { data: agreementDetailsData, isPending: agreementDetailsPending } =
    useStaffGetAgreementDetailsForCampaign(campaignId, !!campaignId && isOpen)
  const {
    data: commissionInformation,
    isPending: commissionInformationPending,
  } = useStaffGetCommissionInfoForCampaign(campaignId, !!campaignId && isOpen)

  const currentCommissionInfo =
    commissionInformation?.commissionInformation?.currentCommissionDetails
      ?.commissionDetails

  const [agreementDetails, setAgreementDetails] = useState<ListingAgreementDetails | undefined>()
  const [agreementDetailsDirty, setAgreementDetailsDirty] = useState(false)
  const [commissionDetails, setCommissionDetails] = useState<CommissionDetails | undefined>()
  const [commissionDetailsDirty, setCommissionDetailsDirty] = useState(false)
  const [commissionDetailsValid, setCommissionDetailsValid] = useState(false)

  useEffect(() => {
    if (!agreementDetailsDirty) setAgreementDetails(agreementDetailsData)
  }, [agreementDetailsData, agreementDetailsDirty])

  useEffect(() => {
    if (!commissionDetailsDirty) setCommissionDetails(currentCommissionInfo)
  }, [commissionDetailsDirty, currentCommissionInfo])

  const invalidateAgreementDetails =
    useInvalidateUseStaffGetAgreementDetailsForCampaign()
  const invalidateCommissionInfo =
    useInvalidateUseStaffGetCommissionInfoForCampaign()

  const invalidateCampaignDetails = () => {
    queryClient.invalidateQueries({
      queryKey: createCampaignDetailsQueryKey(campaignId),
    })
  }

  const { staffUpdateAgreementDetailsForCampaign } =
    useStaffUpdateAgreementDetailsForCampaign({
      onSuccess: async () => {
        await invalidateAgreementDetails(campaignId)
        invalidateCampaignDetails()
      },
      onError: () => {},
    })

  const { staffUpdateCommissionInfoForCampaign } =
    useStaffUpdateCommissionInfoForCampaign({
      onSuccess: async () => {
        await invalidateCommissionInfo(campaignId)
        invalidateCampaignDetails()
      },
      onError: () => {},
    })

  const onAgreementDetailsUpdated = (updated: {
    id: string
    value: ListingAgreementDetails
  }) => {
    const hasChanged =
      JSON.stringify(agreementDetails) !== JSON.stringify(updated.value)

    setAgreementDetails((previous) =>
      JSON.stringify(previous) === JSON.stringify(updated.value)
        ? previous
        : updated.value
    )
    if (hasChanged) setAgreementDetailsDirty(true)
  }

  const onCommissionUpdated = (updated: { value: CommissionDetails }) => {
    const hasChanged =
      JSON.stringify(commissionDetails) !== JSON.stringify(updated.value)

    setCommissionDetails((previous) =>
      JSON.stringify(previous) === JSON.stringify(updated.value)
        ? previous
        : updated.value
    )
    if (hasChanged) setCommissionDetailsDirty(true)
  }

  const isAgreementTypeOpen =
    agreementDetails?.agreementType?.toLowerCase() === 'open'

  const agreementStartDate = agreementDetails?.startDate
  const agreementEndDate = agreementDetails?.endDate
  const hasAgreementType = Boolean(agreementDetails?.agreementType)
  const hasAgreementStart = Boolean(agreementStartDate)
  const hasAgreementEnd = Boolean(agreementEndDate)
  const agreementDatesOrdered =
    !agreementStartDate ||
    !agreementEndDate ||
    dayjs(agreementEndDate).diff(dayjs(agreementStartDate), 'day') >= 0
  const agreementExceededGap =
    !isAgreementTypeOpen &&
    hasAgreementStart &&
    hasAgreementEnd &&
    dayjs(agreementEndDate).diff(dayjs(agreementStartDate), 'day') > 90

  const hasAgreementDetailsTask = tasks.some(
    (task) => task.category?.taskType === TaskCategoryTaskType.AgreementDetails
  )
  const hasAgreementTypeTask = tasks.some(
    (task) =>
      task.category?.taskType === TaskCategoryTaskType.AgreementComplianceType
  )
  const hasCommissionTask = tasks.some(
    (task) => task.category?.taskType === TaskCategoryTaskType.AgreementCommission
  )

  const qldAgreementPassed =
    !hasAgreementTypeTask ||
    (hasAgreementType &&
      hasAgreementStart &&
      (isAgreementTypeOpen || hasAgreementEnd) &&
      agreementDatesOrdered &&
      !agreementExceededGap)
  const nswAgreementPassed =
    !hasAgreementDetailsTask ||
    (hasAgreementStart && hasAgreementEnd && agreementDatesOrdered)
  const qldCommissionPassed = !hasCommissionTask || commissionDetailsValid
  const nswCommissionPassed = !hasAgreementTypeTask || commissionDetailsValid

  const completeDisabled = (() => {
    switch (campaign?.state) {
      case CampaignDetailModelState.QLD:
        return !(qldAgreementPassed && qldCommissionPassed)
      case CampaignDetailModelState.NSW:
        return !(nswAgreementPassed && nswCommissionPassed)
      default:
        return false
    }
  })()

  const saveAgreementDetails = async () => {
    if (!agreementDetailsDirty || !agreementDetails) return

    await staffUpdateAgreementDetailsForCampaign({
      campaignId,
      agreementDetails,
    } as StaffUpdateAgreementDetailsForCampaignRequest)
    setAgreementDetailsDirty(false)
  }

  const saveCommissionDetails = async () => {
    if (!commissionDetailsDirty || !commissionDetails) return

    await staffUpdateCommissionInfoForCampaign({
      campaignId,
      commissionDetails,
      changeType: StaffUpdateCommissionInfoForCampaignRequestChangeType.Other,
      reason: 'Agreement Compliance Completion',
    } as StaffUpdateCommissionInfoForCampaignRequest)
    setCommissionDetailsDirty(false)
  }

  const onSaveCurrentPage = async (task: AutopilotTaskDto) => {
    if (!campaign) return

    if (task.category?.taskType === TaskCategoryTaskType.AgreementDetails) {
      if (campaign.state === CampaignDetailModelState.NSW)
        await saveAgreementDetails()
      return
    }

    if (task.category?.taskType === TaskCategoryTaskType.AgreementComplianceType) {
      if (campaign.state === CampaignDetailModelState.QLD)
        await saveAgreementDetails()
      if (campaign.state === CampaignDetailModelState.NSW)
        await saveCommissionDetails()
      return
    }

    if (
      task.category?.taskType === TaskCategoryTaskType.AgreementCommission &&
      campaign.state === CampaignDetailModelState.QLD
    ) {
      await saveCommissionDetails()
    }
  }

  const renderPage = (props: TaskModalPageProps) =>
    documentsPending ||
    agreementDetailsPending ||
    commissionInformationPending ? (
      <AgxLoadingState />
    ) : (
      <CompliancePages
        {...props}
        agreementDetails={agreementDetails}
        onAgreementDetailsUpdated={onAgreementDetailsUpdated}
        commissionDetails={commissionDetails}
        currentCommissionInfo={currentCommissionInfo}
        setCommissionDetailsValid={setCommissionDetailsValid}
        onCommissionUpdated={onCommissionUpdated}
        isAgreementTypeOpen={isAgreementTypeOpen}
        agreementExceededGap={agreementExceededGap}
      />
    )

  return {
    campaign,
    completeDisabled,
    filteredDocuments,
    onSaveCurrentPage,
    renderPage,
  }
}
